import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Check, Search, X, AlertTriangle } from "lucide-react";

/* --------------------------------------------------------------------- */
/*  Create team                                                           */
/* --------------------------------------------------------------------- */
export function CreateMarketingTeamDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setName(""); setBusy(false); } }, [open]);

  const submit = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      const { data } = await api.post("/marketing-teams", { name: n });
      toast.success("Team created");
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="create-mkt-team-dialog">
        <DialogHeader>
          <DialogTitle>Create Marketing Team</DialogTitle>
          <DialogDescription>Give the team a name. You can rename, add members, and share access later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="mkt-team-name">Team name</Label>
          <Input
            id="mkt-team-name"
            data-testid="create-mkt-team-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Growth Squad"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            data-testid="create-mkt-team-submit"
            onClick={submit}
            disabled={busy || !name.trim()}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {busy ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------------------------- */
/*  Rename team                                                           */
/* --------------------------------------------------------------------- */
export function RenameMarketingTeamDialog({ open, team, onClose, onUpdated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setName(team?.name || ""); }, [team]);

  const submit = async () => {
    const n = name.trim();
    if (!n || !team) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/marketing-teams/${team.id}`, { name: n });
      toast.success("Renamed");
      onUpdated?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="rename-mkt-team-dialog">
        <DialogHeader>
          <DialogTitle>Rename Team</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="mkt-rename">Team name</Label>
          <Input
            id="mkt-rename"
            data-testid="rename-mkt-team-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="rename-mkt-team-submit" onClick={submit} disabled={busy || !name.trim()} className="bg-purple-600 hover:bg-purple-700 text-white">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------------------------- */
/*  Manage members — pick / remove users                                  */
/* --------------------------------------------------------------------- */
export function ManageMarketingMembersDialog({ open, team, users, onClose, onUpdated }) {
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && team) {
      setSelected(team.member_ids || []);
      setQuery("");
    }
  }, [open, team]);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users || [])
      .filter((u) => u.role !== "super_admin" && u.is_active !== false)
      .filter((u) => !q || (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q));
  }, [users, query]);

  const toggle = (uid) => {
    setSelected((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const save = async () => {
    if (!team) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/marketing-teams/${team.id}`, { member_ids: selected });
      toast.success("Members updated");
      onUpdated?.(data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const selectedUsers = (users || []).filter((u) => selected.includes(u.id));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="manage-mkt-members-dialog">
        <DialogHeader>
          <DialogTitle>Team Members — {team?.name}</DialogTitle>
          <DialogDescription>
            Pick people to add; uncheck to remove. Members can view and edit the team's dashboard.
          </DialogDescription>
        </DialogHeader>

        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-1.5 py-1" data-testid="manage-mkt-members-chips">
            {selectedUsers.map((u) => (
              <span key={u.id} className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full border border-purple-200 bg-purple-50 text-xs text-purple-800">
                <Avatar className="h-4 w-4"><AvatarFallback className="bg-purple-600 text-white text-[8px]">{(u.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2)}</AvatarFallback></Avatar>
                {u.name}
                <button onClick={() => toggle(u.id)} className="h-3.5 w-3.5 rounded-full hover:bg-purple-200 flex items-center justify-center" title="Remove">
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people…"
            className="pl-8"
            data-testid="manage-mkt-members-search"
          />
        </div>

        <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
          {candidates.length === 0 && (
            <div className="px-3 py-6 text-sm text-slate-400 text-center">No matching users</div>
          )}
          {candidates.map((u) => {
            const checked = selected.includes(u.id);
            return (
              <label
                key={u.id}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${checked ? "bg-purple-50/60" : ""}`}
                data-testid={`manage-mkt-member-row-${u.id}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(u.id)}
                  className="h-4 w-4 rounded border-slate-300 text-purple-600"
                  data-testid={`manage-mkt-member-check-${u.id}`}
                />
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-purple-100 text-purple-700 text-xs font-semibold">
                    {(u.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                {checked && <Check className="h-4 w-4 text-purple-600" />}
              </label>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="manage-mkt-members-save" onClick={save} disabled={busy} className="bg-purple-600 hover:bg-purple-700 text-white">
            {busy ? "Saving…" : `Save (${selected.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------------------------- */
/*  Delete team — TWO-STEP confirm                                        */
/*  Step 1: scary "are you sure" with a Continue button                   */
/*  Step 2: "ABSOLUTELY sure" with the final Delete button                */
/* --------------------------------------------------------------------- */
export function DeleteMarketingTeamDialog({ open, team, onClose, onDeleted }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setStep(1); setBusy(false); } }, [open]);

  const confirmDelete = async () => {
    if (!team) return;
    setBusy(true);
    try {
      await api.delete(`/marketing-teams/${team.id}`);
      toast.success("Team deleted");
      onDeleted?.(team);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="delete-mkt-team-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            {step === 1 ? "Delete this team?" : "Are you ABSOLUTELY sure?"}
          </DialogTitle>
          <DialogDescription className="pt-2 text-slate-600">
            {step === 1 ? (
              <>You're about to delete <b>{team?.name}</b>. All of its tasks, columns, and shared access will be removed.</>
            ) : (
              <>This is your final confirmation. The team <b>{team?.name}</b> and every row inside it will be permanently deleted. This action cannot be undone.</>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          {step === 1 ? (
            <Button
              data-testid="delete-mkt-team-step1"
              onClick={() => setStep(2)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Continue
            </Button>
          ) : (
            <Button
              data-testid="delete-mkt-team-step2"
              onClick={confirmDelete}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {busy ? "Deleting…" : "Yes, delete it"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
